/* /payroll — payroll overview: history chart, current run totals, compliance alerts, GOSI breakdown, downloads. */
import { Link, useNavigate } from "react-router-dom";
import { usePayrollHistory, usePayrollRun, useSettings } from "../../data/hooks";
import { useOverlays } from "../../app/overlays";
import { useI18n } from "../../i18n";
import { fmtN, MONTHS_AR, MONTHS_EN, periodLabel } from "../../lib/format";
import { Alert, Button, Card, Chip, DualDate, EmptyState, ErrorBox, Grid, HBars, Kpi, LineChart, Money, PageHeader, Skeleton } from "../../ui";
import { useEmpMap } from "./components/common";
import { Art92Alert, MudadAlert, ReportButton, runStatus } from "./components/payroll-bits";
import { curCode, curL, otPct } from "../../lib/country";

export default function Payroll() {
  const { t, L, lang, isAr } = useI18n();
  const nav = useNavigate();
  const { openPrint } = useOverlays();
  const runQ = usePayrollRun();
  const histQ = usePayrollHistory();
  const setQ = useSettings();
  const emps = useEmpMap();

  if (runQ.error) return <ErrorBox error={runQ.error} onRetry={() => runQ.refetch()} />;
  if (histQ.error) return <ErrorBox error={histQ.error} onRetry={() => histQ.refetch()} />;
  if (!runQ.data || !histQ.data) return <Skeleton />;

  const v = runQ.data, run = v.run, tot = v.totals, hist = histQ.data;
  const payDay = setQ.data?.payDay ?? 27;
  const gosi = setQ.data?.gosi;
  const [py, pm] = run.period.split("-").map(Number);
  const monthName = isAr ? MONTHS_AR[pm - 1] : MONTHS_EN[pm - 1];
  const payDate = run.period + "-" + String(payDay).padStart(2, "0");
  const st = runStatus(run);
  const started = run.step > 0 || run.approved;
  const histHasCurrent = hist.some((h) => h.id === run.period);

  const series: [string, number][] = hist.map((h) => [isAr ? MONTHS_AR[h.month - 1].slice(0, 3) : MONTHS_EN[h.month - 1], h.gross]);
  if (!histHasCurrent) series.push([monthName.slice(0, 3), tot.gross]);

  const g = v.slips.reduce((a, s) => ({
    base: a.base + s.gosi.base, empAnn: a.empAnn + s.gosi.empAnn, empSaned: a.empSaned + s.gosi.empSaned,
    erAnn: a.erAnn + s.gosi.erAnn, erSaned: a.erSaned + s.gosi.erSaned, erOH: a.erOH + s.gosi.erOH,
  }), { base: 0, empAnn: 0, empSaned: 0, erAnn: 0, erSaned: 0, erOH: 0 });
  const sum = (k: "basic" | "housing" | "transport" | "other") => Math.round(v.slips.reduce((a, s) => a + s[k], 0));
  const firstEmp = v.slips[0]?.employeeId;

  const rows = [
    ...(histHasCurrent ? [] : [{ id: run.period, year: py, month: pm, gross: tot.gross, employees: tot.headcount, status: st, payDate, cur: true }]),
    ...hist.slice().reverse().map((h) => ({ ...h, cur: h.id === run.period })),
  ];

  return (
    <>
      <PageHeader
        title={t("Payroll")}
        sub={L("Monthly cycle · cut-off 20th · pay day " + payDay + "th · WPS via Mudad", "دورة شهرية · الإقفال يوم 20 · الصرف يوم " + payDay + " · حماية الأجور عبر مدد")}
        actions={<>
          <ReportButton kind="register" file={"Payroll_Register_" + run.period + ".csv"} label={t("Payroll register")} />
          <ReportButton kind="gosi" file={"GOSI_" + run.period + ".csv"} label={L("GOSI report", "تقرير التأمينات")} />
          <Button icon="printer" onClick={() => openPrint({ doc: "register", period: run.period })} data-testid="btn-print-register">{t("Print")}</Button>
          <Button variant="pri" icon="wallet" onClick={() => nav("/payroll/run")} data-testid="btn-run">
            {run.paid ? L("View " + monthName + " run", "عرض مسير " + monthName) : started ? L("Continue " + monthName + " run", "متابعة مسير " + monthName) : L("Start " + monthName + " run", "بدء مسير " + monthName)}
          </Button>
        </>}
      />

      <div className="g g-4 mb">
        <Kpi testId="kpi-gross" label={L(monthName + " gross" + (run.paid ? "" : " (draft)"), "إجمالي " + monthName + (run.paid ? "" : " (مسودة)"))} value={fmtN(tot.gross, 0)} unit={curL(L)} icon="wallet" tone="gold" foot={<Chip status={st} />} />
        <Kpi testId="kpi-net" label={t("Net to bank")} value={fmtN(tot.net, 0)} unit={curL(L)} icon="banknote" foot={L(tot.headcount + " employees", tot.headcount + " موظفًا")} />
        <Kpi testId="kpi-gosi-er" label={L("GOSI · employer", "التأمينات · صاحب العمل")} value={fmtN(tot.gosiEr, 0)} unit={curL(L)} icon="shield" foot={L("Employee share ", "حصة الموظف ") + fmtN(tot.gosiEmp, 0)} />
        <Kpi testId="kpi-ot" label={t("Overtime")} value={fmtN(tot.ot, 0)} unit={curL(L)} icon="clock" tone="info" foot={L("at " + otPct() + " of hourly basic", "بنسبة " + otPct() + " من الأجر بالساعة")} />
      </div>

      <div className="stack mb" data-testid="payroll-alerts">
        <Art92Alert view={v} emp={emps.map} />
        {!run.mudadAck && <MudadAlert issues={v.mudad} emp={emps.map} action={<Button variant="sm" onClick={() => nav("/payroll/run")}>{L("Review", "مراجعة")}</Button>} />}
        {run.paid && <Alert tone="ok" tint testId="paid-alert" title={L(monthName + " payroll is paid", "تم صرف رواتب " + monthName)} />}
      </div>

      <Grid cols="2-1" className="mb">
        <Card title={L("Gross payroll, last 12 months", "إجمالي الرواتب آخر 12 شهرًا")} sub={L("Paid runs + current draft", "المسيرات المدفوعة + المسودة الحالية")} testId="history-chart">
          {series.length >= 2
            ? <LineChart data={series} fmt={(x) => fmtN(x / 1000, 0) + "k"} tip={(x) => fmtN(x, 0)} label={L("Gross payroll history", "سجل إجمالي الرواتب")} />
            : <EmptyState title={L("Not enough history yet", "لا يوجد سجل كافٍ بعد")} />}
        </Card>
        <Card title={L("Cost breakdown · ", "توزيع التكلفة · ") + periodLabel(run.period, lang)} testId="cost-breakdown">
          <HBars data={[[t("Basic salary"), sum("basic")], [t("Housing"), sum("housing")], [t("Transport"), sum("transport")], [L("GOSI employer", "تأمينات صاحب العمل"), Math.round(tot.gosiEr)], [t("Overtime"), Math.round(tot.ot)], [t("Bonus"), Math.round(tot.bonus)]]} />
        </Card>
      </Grid>

      <Grid cols="2-1">
        <Card title={L("Payroll runs", "مسيرات الرواتب")} flush testId="runs-table">
          <div className="tw">
            <table className="tbl cards">
              <thead><tr><th>{t("Period")}</th><th className="end hide-md">{t("Employees count")}</th><th className="end">{t("Gross")}</th><th className="hide-md">{t("Pay date")}</th><th>{t("Status")}</th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="click" data-testid={"run-row-" + r.id} onClick={() => nav(r.cur ? "/payroll/run" : "/payslip/" + firstEmp + "/" + r.id)}>
                    <td className="k1">
                      <Link to={r.cur ? "/payroll/run" : "/payslip/" + firstEmp + "/" + r.id} onClick={(e) => e.stopPropagation()}><b>{periodLabel(r.id, lang)}</b></Link>
                      {r.cur && <span className="xs muted"> {L("current", "الحالي")}</span>}
                    </td>
                    <td className="end hide-md x" data-l={t("Employees count")}><span className="num">{r.employees}</span></td>
                    <td className="end k3" data-l={t("Gross")}><Money value={r.gross} dec={0} /></td>
                    <td className="hide-md x" data-l={t("Pay date")}><DualDate date={r.payDate} /></td>
                    <td className="k2"><Chip status={r.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
        <Card title={L("GOSI breakdown", "تفصيل التأمينات")} sub={gosi ? L("Base = basic + housing, capped at " + curCode() + " " + fmtN(gosi.cap, 0), "الأجر = الأساسي + السكن بحد أقصى " + fmtN(gosi.cap, 0) + " ر.س") : undefined} flush testId="gosi-breakdown">
          <div className="tw">
            <table className="tbl compact">
              <thead><tr><th /><th className="end">{t("Employee")}</th><th className="end">{L("Employer", "صاحب العمل")}</th></tr></thead>
              <tbody>
                <tr><td>{L("Annuities", "المعاشات")}{gosi && <span className="xs muted"> · {gosi.saEmpAnn}% / {gosi.saErAnn}%</span>}</td><td className="end"><Money value={g.empAnn} dec={0} plain /></td><td className="end"><Money value={g.erAnn} dec={0} plain /></td></tr>
                <tr><td>{L("SANED", "ساند")}{gosi && <span className="xs muted"> · {gosi.saEmpSaned}% / {gosi.saErSaned}%</span>}</td><td className="end"><Money value={g.empSaned} dec={0} plain /></td><td className="end"><Money value={g.erSaned} dec={0} plain /></td></tr>
                <tr><td>{L("Occupational hazards", "الأخطار المهنية")}{gosi && <span className="xs muted"> · {gosi.saErOH}%</span>}</td><td className="end">—</td><td className="end"><Money value={g.erOH} dec={0} plain /></td></tr>
              </tbody>
              <tfoot><tr><td>{t("Total")}</td><td className="end" data-testid="gosi-emp-total"><Money value={tot.gosiEmp} dec={0} /></td><td className="end" data-testid="gosi-er-total"><Money value={tot.gosiEr} dec={0} /></td></tr></tfoot>
            </table>
          </div>
          <div className="card-b xs muted">{L("Contribution base ", "أجر الاشتراك ")}<Money value={g.base} dec={0} /></div>
        </Card>
      </Grid>
    </>
  );
}
